import React, { useRef, useState, useEffect } from "react";
import { Send, Loader2, Sparkles } from "lucide-react";
import { streamChat } from "../lib/api";

const SUGGESTIONS = [
  "How can I cut 10% off a 2,400 sq ft home budget?",
  "What's the cost difference between a slab and a basement?",
  "Cheaper siding options that still look premium?",
];

const ChatWidget = () => {
  const [sessionId] = useState(
    () => `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  );
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm your Build Assistant. Ask me anything about construction costs, materials or budgeting for your U.S. or Canadian project.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    try {
      await streamChat(sessionId, content, (chunk) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = {
            role: "assistant",
            content: copy[copy.length - 1].content + chunk,
          };
          return copy;
        });
      });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry, I couldn't reach the assistant. Please try again.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 shadow-xl bg-white overflow-hidden flex flex-col h-[440px]">
      <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#1D4ED8] text-white">
          <Sparkles className="h-4 w-4" />
        </span>
        <span className="text-sm font-semibold text-[#0A2540]">Build Assistant</span>
        <span className="ml-auto flex items-center gap-1.5 text-xs text-emerald-600">
          <span className="h-2 w-2 rounded-full bg-emerald-500" /> Live AI
        </span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user"
                ? "ml-auto rounded-2xl rounded-tr-sm bg-[#1D4ED8] text-white"
                : "rounded-2xl rounded-tl-sm bg-slate-100 text-slate-700"
            }`}
          >
            {m.content || (busy && i === messages.length - 1 ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            ) : "")}
          </div>
        ))}
        {messages.length <= 1 && (
          <div className="flex flex-wrap gap-2 pt-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="text-xs text-left rounded-full border border-slate-200 px-3 py-1.5 text-slate-600 hover:border-[#1D4ED8] hover:text-[#1D4ED8] transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 p-3 flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask about your build…"
          className="flex-1 rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/40"
        />
        <button
          onClick={() => send()}
          disabled={busy}
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#DC2626] text-white hover:bg-[#b91c1c] disabled:opacity-60 transition-colors"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

export default ChatWidget;
